import * as Yup from 'yup';
import { EMAIL, FLOAT, FLOATING_POINT, INTEGER, NAME_TABLE_TAB_SPECIAL, NAME_TABLE_TAB_START, REGEX_INTERGER } from '../@type/global';
import { convertValue } from './checkVariables';
import { MixedSchema } from 'yup/lib/mixed';
export const REGEX_CONFIG = {
    FLOAT: /^[0-9]+(\.[0-9]{0,2})?$/,
    FLOATING_POINT: /^[-+]?[0-9].?[0-9]+([eE][-+]?[0-9]+)?$/,
    INTEGER: /^[0-9]+$/,
    NAME_TABLE_TAB_START: /^[a-zA-Z_]+$/,
    NAME_TABLE_TAB_SPECIAL: /^[a-zA-Z0-9_]*$/,
    REGEX_INTERGER: /[^0-9.-]+/,
    EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    EMAIL_SIMPLE: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    PHONE_INTERNATIONAL: /^[\\+]?[1-9][\d]{0,15}$/,
    PHONE_AUSTRALIAN: /^(?:\+61|0)4\d{8}$/,
    // Firestore document ID - exactly 20 alphanumeric characters
    FIRESTORE_ID: /^[A-Za-z0-9]{20}$/
};

const MAX_INT32 = 2147483647;
const MIN_INT32 = -2147483648;

type PersistableChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> & {
    persist: () => void;
};

type SetFieldValue = (field: string, value: string | number) => void;
type SetEventValue = (event: PersistableChangeEvent) => void;

export function isValidFormatWithRegex(regex: RegExp | string, value: string | number): boolean {
    if (value && regex) {
        const REGEXT = new RegExp(regex);
        return REGEXT.test(String(value));
    }
    return false;
}

export const typeRegex = (type: string): RegExp => {
    switch (type) {
        case FLOAT:
            return REGEX_CONFIG.FLOAT;
        case FLOATING_POINT:
            return REGEX_CONFIG.FLOATING_POINT;
        case INTEGER:
            return REGEX_CONFIG.INTEGER;
        case NAME_TABLE_TAB_START:
            return REGEX_CONFIG.NAME_TABLE_TAB_START;
        case NAME_TABLE_TAB_SPECIAL:
            return REGEX_CONFIG.NAME_TABLE_TAB_SPECIAL;
        case REGEX_INTERGER:
            return REGEX_CONFIG.REGEX_INTERGER;
        case EMAIL:
            return REGEX_CONFIG.EMAIL;
        default:
            return REGEX_CONFIG.FLOAT;
    }
};

export const handleRegexField = (e: PersistableChangeEvent, field: string, type: string, setFieldValue: SetFieldValue): void => {
    const value = e?.target?.value;
    // remove Number(value) because user may input "7."
    // remove value >= 0 because regex will check it
    if (regexFieldCheck(type, value)) {
        e.persist();
        setFieldValue(field, value);
    }
    if (value === '') {
        setFieldValue(field, '');
    }

    if (typeof value === 'number' && value === 0) {
        setFieldValue(field, 0);
    }

    if (typeof value === 'string' && (value === '0' || value.trim() === '0')) {
        setFieldValue(field, 0);
    }
};

export const handleRegexFieldWithEvent = (e: PersistableChangeEvent, type: string, setFieldValue: SetEventValue): void => {
    const value = e?.target?.value;
    if (regexFieldCheck(type, value)) {
        e.persist();
        e.target.value = String(convertValue(type, value));
        setFieldValue(e);
    }
};

export const createNumberValidation = ({
    isRequired = true,
    type = INTEGER,
    isGreaterThanZero = true
}: {
    isRequired?: boolean;
    type?: string;
    isGreaterThanZero?: boolean;
}): Yup.NumberSchema => {
    const validator = Yup.number()
        .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : parseFloat(value)))
        .test('number-type', 'Required field', (value) => {
            if (isRequired) {
                return value !== undefined && value !== null;
            }
            return true;
        })
        .test('number-type', 'Invalid input', (value) => {
            if (value === null || value === undefined) {
                return false;
            }
            if (value < 0) {
                return false;
            }
            if (type === INTEGER) {
                const result = Number.isInteger(value);
                return result;
            }
            if (type === FLOAT) {
                // check is 2 max decimal
                const list = value.toString().split('.');
                if (list.length > 1) {
                    if (list[1].length > 2) {
                        return false;
                    }
                }
            }
            return true;
        })
        .test('greater-than-zero', 'Value must be greater than 0', (value) => {
            if (isGreaterThanZero && typeof value === 'number') {
                return value > 0;
            }
            return true;
        });
    return validator;
};

export const createStrNumberValidation = ({
    isRequired = true,
    type = INTEGER,
    isGreaterThanZero = true,
    maxValue = undefined
}: {
    isRequired?: boolean;
    type?: string;
    isGreaterThanZero?: boolean;
    maxValue?: number;
}): MixedSchema => {
    const validator = Yup.mixed()
        .test('is-required', 'This field is required', (value) => {
            if (isRequired) {
                return value !== undefined && value !== null && value !== '';
            }
            return true;
        })
        .test('is-number', 'Invalid number format', (value) => {
            if (value === undefined || value === null) {
                return !isRequired;
            }
            // return true is passed
            // return false is failed
            if (value === 'Infinite') {
                return true;
            }
            const num = Number(value);
            if (isNaN(num)) {
                return false; // If it's not a number and not empty string
            }

            // Check if the type is INTEGER and the number is not an integer
            if (type === INTEGER && !Number.isInteger(num)) {
                return false;
            }

            if (type === FLOAT && !Number.isInteger(num)) {
                const parts = num.toString().split('.');
                // only 2 decimal allowed
                // 1. is not allowed
                if (parts.length > 1 && parts[1].length === 0) {
                    return false;
                }

                if (parts.length > 1 && parts[1].length > 2) {
                    return false;
                }
            }

            if (isNaN(num)) {
                return true; // Skip this test if not a number (will be caught by is-number test)
            }
            return num >= MIN_INT32 && num <= MAX_INT32;
        })
        .test('greater-than-zero', 'Value must be greater than 0', (value) => {
            if (!isRequired && (value === undefined || value === '')) {
                return true;
            }
            if (value === 'Infinite') {
                return true;
            }

            const num = Number(value);
            if (isGreaterThanZero) {
                return num > 0;
            }
            return num >= 0; // Allow zero if not strictly greater than zero
        })
        .test('less-than-or-equal-to-max', `Value must be less than or equal to ${maxValue}`, (value) => {
            // Skip this test if maxValue is not provided or if value is not required and empty
            if (maxValue === undefined || (!isRequired && (value === undefined || value === ''))) {
                return true;
            }
            if (value === 'Infinite') {
                return false; // Infinite is greater than any finite number
            }

            const num = Number(value);
            return num <= maxValue;
        });

    return validator;
};

export const createListValidation = ({
    isRequired = true,
    minLength = 1
}: {
    isRequired?: boolean;
    minLength?: number;
}): Yup.ArraySchema<Yup.AnySchema> => {
    const validator = Yup.array()
        .test('is-required', 'This field is required', (value) => {
            if (isRequired) {
                return Array.isArray(value) && value.length > 0;
            }
            return true;
        })
        .test('min-length', `Array length must be at least ${minLength}`, (value) => {
            if (!isRequired && (value === undefined || value === null)) {
                return true;
            }
            return Array.isArray(value) && value.length >= minLength;
        });

    return validator;
};

export const createStringValidation = ({
    isRequired = true,
    maxLength = 255,
    minLength = 0
}: {
    isRequired?: boolean;
    maxLength?: number;
    minLength?: number;
}): Yup.StringSchema => {
    let validator = Yup.string()
        .trim()
        .typeError('Please enter a valid string')
        .test('length-check', 'Required field', (value) => {
            if (value === undefined || value === null) {
                return !isRequired;
            }
            const length = value.length;
            return length >= minLength && length <= maxLength;
        });
    if (isRequired) {
        validator = validator.required('This field is required');
    }
    return validator;
};

export const regexFieldCheck = (type: string, value: string): boolean => {
    if (Number(value) === 0) {
        return true;
    }
    return isValidFormatWithRegex(typeRegex(type), value);
};

export const handleRegexEvent = (e: PersistableChangeEvent, type: string): number | string => {
    const value = e?.target?.value;
    const regex = typeRegex(type);
    const numericValue = Number(value);
    if (numericValue && numericValue >= 0 && isValidFormatWithRegex(regex, value)) {
        e.persist();
        return Number(value);
    }
    if (value === '') {
        return '';
    }

    if (typeof value === 'number' && value === 0) {
        return 0;
    }

    if (typeof value === 'string' && (value === '0' || value.trim() === '0')) {
        return 0;
    }
    return '';
};

export const extractFileName = (url: string): string | null => {
    const decodedUrl = decodeURIComponent(url);
    const match = decodedUrl.match(/\/([^/?]+)(?=\?|$)/);
    return match ? match[1] : null;
};

export const extractIndexFromFileName = (fileName: string): number | null => {
    const match = fileName.match(/_(\d+)/);
    return match ? parseInt(match[1], 10) : null;
};

export const validateInputType = (
    input: string
): {
    isFirebaseDocumentId: boolean;
    isEmail: boolean;
    isPhoneNumber: boolean;
    isName: boolean;
} => {
    // Firebase document ID/key validation (fixed length 20, alphanumeric with 62 printable characters)
    const isFirebaseDocumentId = /^[A-Za-z0-9]{20}$/.test(input);

    // Email validation
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);

    // Australian phone number validation (local format: 04xxxxxxxx or international format: +614xxxxxxxx)
    const isPhoneNumber = /^(?:\+61|0)4\d{8}$/.test(input);

    // Name validation (letters, spaces, and basic punctuation)
    const isName = /^[a-zA-Z\s.'-]+$/.test(input);

    return {
        isFirebaseDocumentId,
        isEmail,
        isPhoneNumber,
        isName
    };
};

/**
 * Validate input type for booking search
 * This function determines the type of search input for booking queries
 */
export const validateBookingSearchInput = (
    value: string
): {
    isEmail?: boolean;
    isPhoneNumber?: boolean;
    isFirestoreId?: boolean;
    isName?: boolean;
    isReferenceNumber?: boolean;
} => {
    // if this is a 6-digit number, it is a reference number
    if (/^\d{6}$/.test(value)) {
        return { isReferenceNumber: true };
    }
    if (REGEX_CONFIG.EMAIL_SIMPLE.test(value)) {
        return { isEmail: true };
    }
    if (REGEX_CONFIG.PHONE_INTERNATIONAL.test(value)) {
        return { isPhoneNumber: true };
    }
    if (REGEX_CONFIG.FIRESTORE_ID.test(value)) {
        return { isFirestoreId: true };
    }
    return { isName: true };
};
